import { useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AUTOCOMPLETE_PRODUCT_VARIANT_LABELS from 'api/queries/AUTOCOMPLETE_PRODUCT_VARIANT_LABELS';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import CheckSvg from 'images/v5/icons/check.svg';
import LayersSvg from 'images/v5/icons/layers-two-01.svg';
import Button from 'layout/atoms/Button/Button';
import Input from 'layout/atoms/Input/Input';
import InputLabel from 'layout/atoms/InputLabel';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid/TableGrid';
import Grid from 'layout/molecules/Grid/Grid';
import { addVariants, constructVariants, selectComponentDetails, selectVariantsStatus, } from 'store/componentDetailsSlice';
import { increaseVariantsCounter } from 'store/componentsSlice';
import { closeModal } from 'store/layoutSlice';
import { getAttributeVariantLabel } from 'utils/productParameters';
function MultipleVariantsCreator() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
    const { t, i18n } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const product = useSelector(selectComponentDetails);
    const { construct, add } = useSelector(selectVariantsStatus);
    const { data: productVariantLabels } = useQuery(AUTOCOMPLETE_PRODUCT_VARIANT_LABELS);
    // const [constructVariants] = useMutation(CONSTRUCT_VARIANTS);
    // const [addVariants] = useMutation(ADD_VARIANTS);
    const [colors, setColors] = useState('');
    const [chainColors, setChainColors] = useState('');
    const [tapeColors, setTapeColors] = useState('');
    const [sliderColors, setSliderColors] = useState('');
    const [sizes, setSizes] = useState('');
    const [constructResponse, setConstructResponse] = useState(null);
    const variantColumns = [
        ...(((_a = product === null || product === void 0 ? void 0 : product.category) === null || _a === void 0 ? void 0 : _a.variantHasColor) ? [['100px', '1fr']] : []),
        ...(((_b = product === null || product === void 0 ? void 0 : product.category) === null || _b === void 0 ? void 0 : _b.variantHasSize) ? [['100px', '1fr']] : []),
        ['100px', '1fr'],
    ];
    const handleColorsInputChange = ({ target: { value } }) => {
        setColors(value.toUpperCase());
    };
    const handleChainColorsInputChange = ({ target: { value } }) => {
        setChainColors(value.toUpperCase());
    };
    const handleTapeColorsInputChange = ({ target: { value } }) => {
        setTapeColors(value.toUpperCase());
    };
    const handleSliderColorsInputChange = ({ target: { value } }) => {
        setSliderColors(value.toUpperCase());
    };
    const handleSizesInputChange = ({ target: { value } }) => {
        setSizes(value.toUpperCase());
    };
    const backToGenerate = () => {
        setConstructResponse(null);
    };
    const isGenerationDisabled = () => (product.category.variantHasColor &&
        product.category.colorAutoCalculationMethod === null &&
        !colors) ||
        (product.category.variantHasChainColor && !chainColors) ||
        (product.category.variantHasTapeColor && !tapeColors) ||
        (product.category.variantHasSliderColor && !sliderColors) ||
        (product.category.variantHasSize && !sizes);
    registerDataValidationErrorMatcher();
    const handleGenerateVariants = () => {
        if (!isGenerationDisabled()) {
            dispatch(constructVariants({
                productId: product.id,
                colors,
                sizes,
                chainColors,
                tapeColors,
                sliderColors,
            }))
                .unwrap()
                .then((response) => {
                setConstructResponse(response);
            });
        }
    };
    const handleCreatorClose = () => {
        dispatch(closeModal({ name: 'multipleVariantsCreator' }));
        setConstructResponse(null);
    };
    const handleAddVariants = () => {
        dispatch(addVariants({
            productId: product.id,
            variants: constructResponse === null || constructResponse === void 0 ? void 0 : constructResponse.variantsToAdd.map((value) => ({
                color: value.color,
                chainColor: value.chainColor,
                tapeColor: value.tapeColor,
                sliderColor: value.sliderColor,
                size: value.size,
                toCreate: value.toCreate,
            })),
        }))
            .unwrap()
            .then(() => {
            Array.from({ length: (constructResponse === null || constructResponse === void 0 ? void 0 : constructResponse.variantsToAdd.length) || 0 }).forEach(() => {
                dispatch(increaseVariantsCounter({ id: product.id }));
            });
            handleCreatorClose();
        });
    };
    return (React.createElement(Modal, { name: "multipleVariantsCreator", size: "s", header: React.createElement(Label, { weight: "strong" },
            React.createElement(LayersSvg, null),
            t('add_multiple_variants')), footer: React.createElement(React.Fragment, null,
            constructResponse === null && (React.createElement(React.Fragment, null,
                React.createElement(Button, { theme: "ghost", onClick: handleCreatorClose }, t('cancel')),
                React.createElement(Button, { color: "primary", leftIcon: React.createElement(CheckSvg, null), onClick: handleGenerateVariants, disabled: isGenerationDisabled(), loading: construct === 'loading' }, t('generate_variants_phase_1')))),
            constructResponse !== null && !constructResponse.constructionSuccessful && (React.createElement(React.Fragment, null,
                React.createElement(Button, { theme: "ghost", onClick: handleCreatorClose }, t('cancel')),
                React.createElement(Button, { theme: "fill", onClick: backToGenerate }, t('generate_variants.back')))),
            constructResponse !== null && constructResponse.constructionSuccessful && (React.createElement(React.Fragment, null,
                React.createElement(Button, { theme: "ghost", onClick: handleCreatorClose }, t('cancel')),
                React.createElement(Button, { onClick: backToGenerate }, t('generate_variants.back')),
                React.createElement(Button, { color: "primary", leftIcon: React.createElement(CheckSvg, null), onClick: handleAddVariants, loading: add === 'loading' }, t('generate_variants.confirm'))))) },
        constructResponse === null && (React.createElement(Grid, null,
            (!product.category.variantHasColor || !product.category.variantHasSize) && (React.createElement("p", { style: { margin: '0', lineHeight: '1.33' } }, t('multiple_variants_generate_single_input'))),
            product.category.variantHasColor && product.category.variantHasSize && (React.createElement("p", { style: { margin: '0', lineHeight: '1.33' } }, t('multiple_variants_generate_multiple_inputs'))),
            ((_c = product === null || product === void 0 ? void 0 : product.category) === null || _c === void 0 ? void 0 : _c.variantHasSize) && (React.createElement("div", null,
                React.createElement(InputLabel, { text: getAttributeVariantLabel(i18n, productVariantLabels, 'size', (_d = product === null || product === void 0 ? void 0 : product.category) === null || _d === void 0 ? void 0 : _d.type) }),
                React.createElement(Input, { name: "size", value: sizes, onChange: handleSizesInputChange }))),
            ((_e = product === null || product === void 0 ? void 0 : product.category) === null || _e === void 0 ? void 0 : _e.variantHasColor) &&
                ((_f = product === null || product === void 0 ? void 0 : product.category) === null || _f === void 0 ? void 0 : _f.colorAutoCalculationMethod) === null && (React.createElement("div", null,
                React.createElement(InputLabel, { text: getAttributeVariantLabel(i18n, productVariantLabels, 'color', (_g = product === null || product === void 0 ? void 0 : product.category) === null || _g === void 0 ? void 0 : _g.type) }),
                React.createElement(Input, { name: "color", value: colors, onChange: handleColorsInputChange }))),
            ((_h = product === null || product === void 0 ? void 0 : product.category) === null || _h === void 0 ? void 0 : _h.variantHasTapeColor) && (React.createElement("div", null,
                React.createElement(InputLabel, { text: getAttributeVariantLabel(i18n, productVariantLabels, 'tapeColor', (_j = product === null || product === void 0 ? void 0 : product.category) === null || _j === void 0 ? void 0 : _j.type) }),
                React.createElement(Input, { name: "tapeColor", value: tapeColors, onChange: handleTapeColorsInputChange }))),
            ((_k = product === null || product === void 0 ? void 0 : product.category) === null || _k === void 0 ? void 0 : _k.variantHasSliderColor) && (React.createElement("div", null,
                React.createElement(InputLabel, { text: getAttributeVariantLabel(i18n, productVariantLabels, 'sliderColor', (_l = product === null || product === void 0 ? void 0 : product.category) === null || _l === void 0 ? void 0 : _l.type) }),
                React.createElement(Input, { name: "sliderColor", value: sliderColors, onChange: handleSliderColorsInputChange }))),
            ((_m = product === null || product === void 0 ? void 0 : product.category) === null || _m === void 0 ? void 0 : _m.variantHasChainColor) && (React.createElement("div", null,
                React.createElement(InputLabel, { text: getAttributeVariantLabel(i18n, productVariantLabels, 'chainColor', (_o = product === null || product === void 0 ? void 0 : product.category) === null || _o === void 0 ? void 0 : _o.type) }),
                React.createElement(Input, { name: "chainColor", value: chainColors, onChange: handleChainColorsInputChange }))))),
        constructResponse !== null && !constructResponse.constructionSuccessful && (React.createElement(Grid, null,
            React.createElement("p", null,
                t('too_many_variants.part_1'),
                "  ",
                constructResponse.toCreateCount),
            React.createElement("p", null,
                t('too_many_variants.part_2'),
                "  ",
                constructResponse.toCreateLimit))),
        constructResponse !== null && constructResponse.constructionSuccessful && (React.createElement(Grid, null,
            React.createElement("p", null,
                t('variants_to_create_header'),
                "  ",
                constructResponse.toCreateCount),
            React.createElement(TableGrid, { columns: variantColumns },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        ((_p = product === null || product === void 0 ? void 0 : product.category) === null || _p === void 0 ? void 0 : _p.variantHasColor) && (React.createElement(Cell, null, getAttributeVariantLabel(i18n, productVariantLabels, 'color', (_q = product === null || product === void 0 ? void 0 : product.category) === null || _q === void 0 ? void 0 : _q.type))),
                        ((_r = product === null || product === void 0 ? void 0 : product.category) === null || _r === void 0 ? void 0 : _r.variantHasSize) && (React.createElement(Cell, null, getAttributeVariantLabel(i18n, productVariantLabels, 'size', (_s = product === null || product === void 0 ? void 0 : product.category) === null || _s === void 0 ? void 0 : _s.type))),
                        React.createElement(Cell, null, t('toCreate.header')))),
                React.createElement(Body, null, constructResponse.variantsToAdd.map((variantToCreate) => {
                    var _a, _b;
                    return (React.createElement(Row, { key: variantToCreate.id },
                        ((_a = product === null || product === void 0 ? void 0 : product.category) === null || _a === void 0 ? void 0 : _a.variantHasColor) && React.createElement(Cell, null, variantToCreate.color),
                        ((_b = product === null || product === void 0 ? void 0 : product.category) === null || _b === void 0 ? void 0 : _b.variantHasSize) && React.createElement(Cell, null, variantToCreate.size),
                        React.createElement(Cell, null, variantToCreate.toCreate ? t('toCreate.true') : t('toCreate.false'))));
                })))))));
}
export default MultipleVariantsCreator;
